import { AgTechDataAction } from "@agtech/core/data/actions/AgTechDataActions";
import { useAgTechHttpDeleteEntityRequest } from "@agtech/core/data/http/AgTechApiDeleteRequests";
import { useAgTechHttpSubmitEntityRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import { ContactNote } from "./ContactNoteData";
import dayjs from "dayjs";
import { useAgTechPopUpContext } from "@agtech/core/functions/AgTechAppPopUps";
import { ContactDataGetContactDetailsQuery } from "../ContactDataQueries";
import { Index } from "@agtech/core/utilities/AgTechUtilities";

export const useContactNoteDataWriteNoteAction = (contactId: number): AgTechDataAction<ContactNote, ContactNote> => {
    const writeNoteSubmissionRequest = useAgTechHttpSubmitEntityRequest<ContactNote>();

    return {
        name: 'WriteNote',
        getConfiguration: props => ({
            actionExecutionMessage: props.originalEntity?.id ? 'Updating note...' : 'Creating note...',
            actionConfirmationMessage: `Successfully ${props.originalEntity?.id ? 'updated' : 'created'} note`,
            showConfirmationMessage: true,
            actionConfirmationDelay: 500
        }),
        validate: async props => {
            props.validation.failWithWarningIf(!props.submittedEntity.note, 'Please specify content for this note.');
        },
        action: async props => {
            return await writeNoteSubmissionRequest.post({
                path: 'ContactNote',
                postedData: {
                    ...props.submittedEntity,
                    date: dayjs(props.submittedEntity.date).format('YYYY-MM-DD'),
                    contactId: contactId
                }
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: ContactDataGetContactDetailsQuery,
                queryContext: { contactId },
                update: contactDetails => {
                    return {
                        ...contactDetails,
                        notes: {
                            ...contactDetails.notes,
                            [props.responseData.id]: props.responseData
                        }
                    }
                }
            });
        }
    }
}

export const useContactNoteDataDeleteNoteAction = (): AgTechDataAction<ContactNote, boolean> => {
    const contactNoteDeleteRequest = useAgTechHttpDeleteEntityRequest();
    const popupContext = useAgTechPopUpContext();

    return {
        name: 'DeleteNote',
        getConfiguration: () => ({
            actionExecutionMessage: 'Deleting note...',
            actionConfirmationMessage: `Successfully deleted note`,
            showConfirmationMessage: true,
            actionConfirmationDelay: 300
        }),
        confirm: async props => {
            return await props.appContext.confirmations.askForConfirmation({
                content: {
                    header: 'Are you sure you want to delete this note?'
                },
                actions: {
                    onConfirm: async () => true,
                    onCancel: async () => false
                }
            })
        },
        action: async props => {
            return await contactNoteDeleteRequest.deleteEntity({
                controller: 'ContactNote',
                entityId: props.submittedEntity.id,
            });
        },
        onSuccess: async props => {
            props.updateQueryData({
                query: ContactDataGetContactDetailsQuery,
                queryContext: { contactId: props.submittedEntity.contactId },
                update: contactDetails => {
                    const updatedNotes = Object.values(contactDetails.notes)
                        .filter(note => note.id !== props.submittedEntity.id);
                    
                    return {
                        ...contactDetails,
                        notes: Index(updatedNotes, note => note.id)
                    }
                }
            });

            popupContext?.closePopUp();
        }
    }
}